import type { AppStatus } from "@/types/borrower";

/**
 * Stable tab keys for the admin application detail page.
 * Labels may change; keys stay so localStorage and deep-links keep working.
 */
export const ADMIN_APP_TAB_KEYS = [
  "application",
  "ai-analysis",
  "default-history",
  "transactions",
  "merchant-tasks",
  "final-uw",
  "position-finder",
] as const;

export type AdminAppTabKey = (typeof ADMIN_APP_TAB_KEYS)[number];

/** Retired tab keys and the tab that now holds their content. */
export const ADMIN_APP_TAB_ALIASES: Readonly<Record<string, AdminAppTabKey>> = {
  // Baselayer moved into Background Check, on the KYB/KYC chip.
  baselayer: "default-history",
};

/** The chips inside the Background Check tab (key "default-history"), in display order. */
export const BACKGROUND_CHECK_CHIPS = ["history", "kyb"] as const;
export type BackgroundCheckChip = (typeof BACKGROUND_CHECK_CHIPS)[number];
export const BACKGROUND_CHECK_CHIP_STORAGE_KEY = "admin_app_background_check_chip_v1";

/** The chip each retired tab key opens on, since the default chip would not hold its content. */
const BACKGROUND_CHECK_CHIP_ALIASES: Readonly<Record<string, BackgroundCheckChip>> = {
  baselayer: "kyb",
};

/**
 * The chip Background Check opens on when none is stored for this deal: the first chip,
 * unless the deal's stored tab is a retired key that lives on another chip.
 */
export function defaultBackgroundCheckChip(id: string | undefined): BackgroundCheckChip {
  const stored = readStoredAdminTab();
  const retiredTab = id != null && stored?.id === id ? stored.tab : undefined;
  return (retiredTab && BACKGROUND_CHECK_CHIP_ALIASES[retiredTab]) || BACKGROUND_CHECK_CHIPS[0];
}

/** Open the Background Check tab on a given chip next time this deal is shown. */
export function writeBackgroundCheckChip(id: string | number, chip: BackgroundCheckChip): void {
  localStorage.setItem(
    BACKGROUND_CHECK_CHIP_STORAGE_KEY,
    JSON.stringify({ id: String(id), tab: chip })
  );
}

/** Bumped when the stored shape gained `status` and Fraud Detection was removed. */
export const ADMIN_APP_DETAIL_TAB_STORAGE_KEY = "admin_app_detail_active_tab_v6";

/** Contract-ready through Plaid-completed (inclusive) — land on Merchant Tasks. */
const MERCHANT_TASKS_DEFAULT_STATUSES: ReadonlySet<string> = new Set<AppStatus>([
  "contract_ready",
  "contract_sent",
  "contract_signed",
  "bank_verification_required",
  "plaid_initiated",
  "plaid_completed",
]);

/** Past Final Underwriting — that tab is the home even before its payload is inspected. */
const FINAL_UW_HOME_STATUSES: ReadonlySet<string> = new Set<AppStatus>([
  "funded",
  "repayment",
  "defaulted",
  "paid_off",
  "refi_ready",
]);

/** Statuses with their own landing tab. Everything else uses AI Analysis / Final UW. */
function outlinedAdminAppTab(status: string | null | undefined): AdminAppTabKey | null {
  if (status === "funder_review") return "ai-analysis";
  if (status != null && MERCHANT_TASKS_DEFAULT_STATUSES.has(status)) return "merchant-tasks";
  if (status === "final_underwriting") return "final-uw";
  return null;
}

/**
 * Enough of a deal to tell whether Final Underwriting has something to show.
 * A Plaid lane (report or analysis) means the tab was used; a statements-only
 * decline from Funder Review does not.
 */
export interface FinalUwPresence {
  decisions?: {
    data_source?: string | null;
    detailed_results?: { analysis?: unknown } | null;
  }[];
  plaid_asset_report_pdf_url?: string | null;
  plaid_report_status?: string | null;
}

export function finalUnderwritingTabHasData(borrower: FinalUwPresence | null | undefined): boolean {
  if (!borrower) return false;
  if (borrower.plaid_asset_report_pdf_url) return true;
  if (borrower.plaid_report_status === "ready" || borrower.plaid_report_status === "processing") {
    return true;
  }
  return (borrower.decisions ?? []).some(
    (decision) => decision.data_source === "plaid" && decision.detailed_results?.analysis != null
  );
}

/**
 * Landing tab from status alone (list click / warm). Statuses without an
 * outlined default open AI Analysis, except post-funding statuses which open
 * Final Underwriting.
 */
export function defaultAdminAppTabForStatus(status: string | null | undefined): AdminAppTabKey {
  return (
    outlinedAdminAppTab(status) ??
    (status != null && FINAL_UW_HOME_STATUSES.has(status) ? "final-uw" : "ai-analysis")
  );
}

/**
 * Same as {@link defaultAdminAppTabForStatus}, but an unoutlined status
 * (declined, failed, early pipeline, …) opens Final Underwriting when that
 * tab already has Plaid data.
 */
export function landingAdminAppTab(
  status: string | null | undefined,
  borrower?: FinalUwPresence | null
): AdminAppTabKey {
  const outlined = outlinedAdminAppTab(status);
  if (outlined) return outlined;
  if (status != null && FINAL_UW_HOME_STATUSES.has(status)) return "final-uw";
  if (finalUnderwritingTabHasData(borrower)) return "final-uw";
  return "ai-analysis";
}

interface StoredAdminTab {
  id?: string;
  tab?: string;
  status?: string;
}

function readStoredAdminTab(): StoredAdminTab | null {
  try {
    const raw = localStorage.getItem(ADMIN_APP_DETAIL_TAB_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAdminTab;
  } catch {
    return null;
  }
}

/** A current tab key, or a retired one the page still sends to the tab holding its content. */
export function isKnownAdminTab(tab: string | undefined): boolean {
  if (tab == null) return false;
  const current = ADMIN_APP_TAB_ALIASES[tab] ?? tab;
  return typeof current === "string" && (ADMIN_APP_TAB_KEYS as readonly string[]).includes(current);
}

/** Resolve a stored/alias key to a live tab, or null if unknown. */
export function resolveAdminAppTab(tab: string | undefined): AdminAppTabKey | null {
  if (!isKnownAdminTab(tab) || tab == null) return null;
  return (ADMIN_APP_TAB_ALIASES[tab] ?? tab) as AdminAppTabKey;
}

/**
 * One-shot read of the seeded tab for this deal (warm / deep-link). Given the deal's status, a
 * tab seeded at another status is stale (the deal moved on since) and is not read.
 */
export function readSeededAdminAppTab(
  id: string | undefined,
  status?: string | null
): AdminAppTabKey | null {
  if (!id) return null;
  const stored = readStoredAdminTab();
  if (stored?.id !== id) return null;
  if (status != null && stored.status != null && stored.status !== status) return null;
  return resolveAdminAppTab(stored.tab);
}

/**
 * Before navigating to a deal, seed localStorage with that status's default tab
 * so the detail page's first paint does not flash. Does not preserve a previously
 * selected tab — landing is always status-based (use {@link writeAdminAppDetailTab}
 * for one-shot deep links like Edit → Application).
 */
export function warmAdminAppDetailTab(
  id: string | number,
  status: string | null | undefined
): void {
  writeAdminAppDetailTab(id, defaultAdminAppTabForStatus(status), status);
}

export function writeAdminAppDetailTab(
  id: string | number,
  tab: AdminAppTabKey,
  status?: string | null
): void {
  localStorage.setItem(
    ADMIN_APP_DETAIL_TAB_STORAGE_KEY,
    JSON.stringify({
      id: String(id),
      tab,
      ...(status != null ? { status } : {}),
    })
  );
}
